import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import React, { Suspense, lazy } from 'react';

// CRITICAL PATH — eager load, first paint
import ProfessionalLanding from './pages/ProfessionalLanding';
import Login from './pages/Login';

// EVERYTHING ELSE — lazy loaded so first paint doesn't wait for admin/dashboard JS
const NewLandingPage = lazy(() => import('./pages/NewLandingPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const EnhancedDashboard = lazy(() => import('./pages/EnhancedDashboard'));
const AdvancedCampaigns = lazy(() => import('./pages/AdvancedCampaigns'));
const CampaignDetails = lazy(() => import('./pages/CampaignDetails'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Reports = lazy(() => import('./pages/Reports'));
const UpdatedPricing = lazy(() => import('./pages/UpdatedPricing'));
const ComsignGenerator = lazy(() => import('./pages/ComsignGenerator'));
const CryptoCheckout = lazy(() => import('./pages/CryptoCheckout'));
const Checkout = lazy(() => import('./pages/Checkout'));
const SubscriptionRequired = lazy(() => import('./pages/SubscriptionRequired'));
const SessionRecordings = lazy(() => import('./pages/SessionRecordings'));
const AdvancedAnalytics = lazy(() => import('./pages/AdvancedAnalytics'));
const ThreatDashboard = lazy(() => import('./pages/ThreatDashboard'));
const InstallationGuide = lazy(() => import('./pages/InstallationGuide'));
const ThreatIntelligence = lazy(() => import('./pages/ThreatIntelligence'));
const AdvancedProtection = lazy(() => import('./pages/AdvancedProtection'));
const ReferralProgram = lazy(() => import('./pages/ReferralProgram'));
const SubAccounts = lazy(() => import('./pages/SubAccounts'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Documentation = lazy(() => import('./pages/Documentation'));
const ApiReference = lazy(() => import('./pages/ApiReference'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const AcceptableUsePolicy = lazy(() => import('./pages/AcceptableUsePolicy'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const TutorialsPage = lazy(() => import('./pages/TutorialsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
import GoldParticles from './components/GoldParticles';
import GoldenCursorTrail from './components/GoldenCursorTrail';
import TelegramSupport from './components/TelegramSupport';
import '@/App.css';

// Error Boundary to catch and display errors instead of white page
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
          <div className="max-w-lg text-center">
            <h1 className="text-2xl font-bold mb-4 text-red-500">Something went wrong</h1>
            <p className="text-gray-400 mb-4">Please refresh the page or contact support.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-2 bg-amber-500 text-black rounded-lg font-semibold"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground font-mono">Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

// Route that requires both authentication AND active paid subscription
function SubscriptionRoute({ children }) {
  const { user, loading, hasActiveSubscription, subscription } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground font-mono">Loading...</div>
      </div>
    );
  }
  
  // If not logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // If admin, bypass subscription check
  if (user.role === 'admin') {
    return children;
  }
  
  // If no active paid subscription, redirect to subscription page
  if (!hasActiveSubscription()) {
    return <Navigate to="/subscription" />;
  }
  
  return children;
}

function AppRoutes() {
  const { user, hasActiveSubscription } = useAuth();

  const RouteFallback = (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-yellow-400 font-mono text-sm">Loading…</div>
    </div>
  );

  return (
    <Suspense fallback={RouteFallback}>
    <Routes>
      <Route path="/" element={<ProfessionalLanding />} />
      <Route path="/login" element={user ? (hasActiveSubscription() || user?.role === 'admin' ? <Navigate to="/dashboard" /> : <Navigate to="/subscription" />) : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />
      <Route path="/pricing" element={<UpdatedPricing />} />
      {/* Subscription page - requires login but not subscription */}
      <Route path="/subscription" element={<PrivateRoute><SubscriptionRequired /></PrivateRoute>} />
      <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
      <Route path="/checkout/crypto" element={<PrivateRoute><CryptoCheckout /></PrivateRoute>} />
      {/* Protected routes - require both login AND active subscription */}
      <Route path="/dashboard" element={<SubscriptionRoute><EnhancedDashboard /></SubscriptionRoute>} />
      <Route path="/campaigns" element={<SubscriptionRoute><AdvancedCampaigns /></SubscriptionRoute>} />
      <Route path="/campaigns/:id" element={<SubscriptionRoute><CampaignDetails /></SubscriptionRoute>} />
      <Route path="/safe-pages" element={<SubscriptionRoute><ComsignGenerator /></SubscriptionRoute>} />
      <Route path="/reports" element={<SubscriptionRoute><Reports /></SubscriptionRoute>} />
      <Route path="/recordings" element={<SubscriptionRoute><SessionRecordings /></SubscriptionRoute>} />
      <Route path="/analytics" element={<SubscriptionRoute><AdvancedAnalytics /></SubscriptionRoute>} />
      <Route path="/threats" element={<SubscriptionRoute><ThreatDashboard /></SubscriptionRoute>} />
      <Route path="/threat-intel" element={<SubscriptionRoute><ThreatIntelligence /></SubscriptionRoute>} />
      <Route path="/advanced-protection" element={<SubscriptionRoute><AdvancedProtection /></SubscriptionRoute>} />
      <Route path="/installation-guide" element={<SubscriptionRoute><InstallationGuide /></SubscriptionRoute>} />
      <Route path="/admin" element={<SubscriptionRoute><AdminPanel /></SubscriptionRoute>} />
      <Route path="/referrals" element={<SubscriptionRoute><ReferralProgram /></SubscriptionRoute>} />
      <Route path="/sub-accounts" element={<SubscriptionRoute><SubAccounts /></SubscriptionRoute>} />
      <Route path="/help" element={<SubscriptionRoute><HelpCenter /></SubscriptionRoute>} />
      {/* Footer Pages - Public */}
      <Route path="/docs" element={<Documentation />} />
      <Route path="/api-reference" element={<ApiReference />} />
      <Route path="/support" element={<Support />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
      <Route path="/gdpr" element={<PrivacyPolicy />} />
      <Route path="/about" element={<Support />} />
      <Route path="/contact" element={<Support />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/careers" element={<Support />} />
      <Route path="/status" element={<Support />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/tutorials" element={<TutorialsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <div className="App min-h-screen">
          <GoldParticles />
          <Toaster position="top-right" theme="dark" />
          <AuthProvider>
            <BrowserRouter>
              <GoldenCursorTrail />
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
          {/* Global Telegram Support Widget - appears on all pages */}
          <TelegramSupport />
        </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;